import './style.modules.scss'

import { Link, Loading } from '@ampcore/adel-react'
import React, { useEffect } from 'react'

import { dispatchCustomEvent } from '../../hooks/useEventListener'
import { CustomEventType } from '../../types/events/CustomEventType.enum'
import { Pagination } from '../Pagination/Pagination'
import { SearchBox } from '../SearchBox/SearchBox'
import { SearchResultItem } from './SearchResultItem'
import { useSearchResults } from './useSearchResults'

export type SearchResultsProps = {
  dataOverride?: Partial<ReturnType<typeof useSearchResults>>
  noResultsMessage?: string
  testId?: string
}

export const SearchResults = ({
  dataOverride,
  noResultsMessage,
  testId
}: SearchResultsProps) => {
  const hookData = useSearchResults()
  const data = { ...hookData, ...dataOverride }
  const {
    suggestedQuery,
    searchResults,
    paginationData,
    queryTermsRegExp,
    searchQueryDecoded,
    searchScrubbed,
    isLoaded,
    error
  } = data
  const origin = window.location.origin
  const searchUrl = `${origin}/search`
  const searchAutocompleteUrl = `${origin}/api/ampf/autocomplete/`

  const ErrorMessage = (
    <p data-testid={testId ? `${testId}-error` : undefined}>
      Site search is temporarily unavailable. <br /> Please try again later. For
      immediate assistance,
      <Link
        className="u-marginLeft4"
        url="/customer-service/contact/"
        name="contact us."
      />
    </p>
  )

  const SearchScrubbedNotice = (
    <p data-testid={testId ? `${testId}-scrubbed-notice` : undefined}>
      For security reasons, some characters in your search query were removed.
    </p>
  )

  useEffect(() => {
    if (!searchResults || searchResults.length === 0 || error) {
      dispatchCustomEvent(CustomEventType.NO_SEARCH_RESULTS)
    }
  }, [searchResults, error])

  const getPageHref = (page: number) => {
    const pageStartItemIndex = (page - 1) * 10
    return `${paginationData?.urlHostPage}${paginationData?.currentSearchUrl}&sp_n=${pageStartItemIndex}`
  }

  const renderResultsSummary = () => {
    if (!paginationData) return null

    const { resultsLower, resultsUpper, resultsTotal } = paginationData
    const upperBound = Math.min(resultsUpper ?? 0, resultsTotal ?? 0)
    const resultMessage =
      resultsTotal === 0
        ? 'Showing 0 results'
        : ` Showing results ${resultsLower}–${upperBound} of ${resultsTotal}`
    const queryText = searchQueryDecoded.replace(/[()]/g, '"')
    return (
      <h4
        className="u-textSizeMsrp2"
        data-testid={testId ? `${testId}-results-summary` : undefined}
      >
        {resultMessage} for <mark>"{queryText}"</mark>
      </h4>
    )
  }

  const renderResultsList = () => {
    if (searchResults.length === 0)
      return (
        <h3
          className="u-textColorNeutral u-textItalic u-textSizeMsrp4 u-sm-textSizeMsrp3 u-textNormal u-marginTop32"
          data-testid={testId ? `${testId}-no-results` : undefined}
        >
          {noResultsMessage ||
            "Sorry, we couldn't find what you're looking for."}{' '}
          {suggestedQuery && (
            <span
              data-testid={testId ? `${testId}-spelling-suggestion` : undefined}
            >
              Did you mean "
              <a href={`${searchUrl}?sp_q=${suggestedQuery}`}>
                {suggestedQuery}
              </a>
              "?
            </span>
          )}
        </h3>
      )

    return (
      <ul
        className="u-marginTop32 u-marginBottom40"
        data-testid={testId ? `${testId}-results-list` : undefined}
      >
        {searchResults.map(result => (
          <SearchResultItem
            key={result.documentId}
            result={result}
            queryTermsRegExp={queryTermsRegExp}
          />
        ))}
      </ul>
    )
  }

  const shouldRenderResults = error === ''
  return (
    <div className="SearchResults" data-testid={testId ? testId : undefined}>
      <section className="SearchResults-content u-sizeConstrained">
        <h1 className="u-textSizeMsrp7 u-sm-textSizeMsrp6 u-paddingBottom24">
          Search Results
        </h1>
        <div className="u-paddingBottom32">
          <SearchBox
            label="Search Ameriprise.com"
            searchUrl={searchUrl}
            searchAutocompleteUrl={searchAutocompleteUrl}
            submitBtnClassName="u-sm-hidden"
            testId="search-box"
          />
        </div>
        {!isLoaded && (
          <div
            className="u-flex u-flexCol u-flexAlignItemsCenter u-paddingTop48"
            data-testid={testId ? `${testId}-loading` : undefined}
          >
            <Loading className=" u-marginLeft4 u-textSizeMsrp2" />
          </div>
        )}
        {isLoaded && (
          <>
            {error && ErrorMessage}
            {searchScrubbed && SearchScrubbedNotice}
            {shouldRenderResults && (
              <>
                {renderResultsSummary()}
                {renderResultsList()}
                {paginationData && paginationData.resultsTotal > 0 && (
                  <div className="u-flex u-flexJustifyCenter u-margin8">
                    <Pagination
                      totalPages={paginationData.totalPages}
                      currentPage={paginationData.currentPageIndex}
                      getPageHref={getPageHref}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  )
}
